import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Pencil, X, MoreVertical, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { dealService } from '../services/dealService';
import { contactService } from '../services/contactService';
import { unitService } from '../services/unitService';
import * as noteService from '../services/noteService';
import api from '../services/api';
import DocumentGallery from './DocumentGallery';
import { Deal } from '../types/deal';
import { Contact } from '../types/contact';
import { Unit } from '../types/unit';
import '../styles/DealDetailsSlider.css';

interface DealDetailsSliderProps {
  workspaceId: string;
  dealId?: string;
  isNew?: boolean;
  onClose: () => void;
}

const DealDetailsSlider: React.FC<DealDetailsSliderProps> = ({
  workspaceId,
  dealId,
  isNew = false,
  onClose
}) => {
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('deal-info');

  // Dates editing
  const [editingDates, setEditingDates] = useState(false);
  const [datesData, setDatesData] = useState({
    initiatedOn: '',
    signedOn: '',
    executedOn: '',
    acknowledgementDate: '',
    firmedOn: '',
    extensionDays: '',
    coolingExtendedPeriod: '',
    reinstateDate: '',
    keyPickupDateTime: '',
    reservedOn: '',
    soldOn: '',
    closedOn: ''
  });
  const [savingDates, setSavingDates] = useState(false);

  // Contact editing (Broker, Sales Agent, Solicitor)
  const [editingBroker, setEditingBroker] = useState(false);
  const [editingAgent, setEditingAgent] = useState(false);
  const [editingSolicitor, setEditingSolicitor] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Contact[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [savingContact, setSavingContact] = useState(false);
  const [currentContactType, setCurrentContactType] = useState<string>('');

  // Third Party, Conditional Deal, Corporation editing
  const [editingThirdParty, setEditingThirdParty] = useState(false);
  const [thirdPartyData, setThirdPartyData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    displayName: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    fax: '',
    natureOfBusiness: ''
  });
  const [savingThirdParty, setSavingThirdParty] = useState(false);

  const [editingConditionalDeal, setEditingConditionalDeal] = useState(false);
  const [conditionalDealData, setConditionalDealData] = useState({
    conditionsRemoved: false,
    conditionsRemovedOn: '',
    conditions: ''
  });
  const [savingConditionalDeal, setSavingConditionalDeal] = useState(false);

  const [editingCorporation, setEditingCorporation] = useState(false);
  const [corporationData, setCorporationData] = useState({
    name: '',
    natureOfBusiness: '',
    directorNames: '',
    verified: false,
    verifiedSource: '',
    registrationNo: '',
    formReceived: false
  });
  const [savingCorporation, setSavingCorporation] = useState(false);

  // Notes
  const [notes, setNotes] = useState<noteService.Note[]>([]);
  const [addingNote, setAddingNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Parking/Locker/Bicycle Allocation
  const [showParkingModal, setShowParkingModal] = useState(false);
  const [showLockerModal, setShowLockerModal] = useState(false);
  const [showBicycleModal, setShowBicycleModal] = useState(false);
  const [parkingAllocations, setParkingAllocations] = useState<Array<{ type: string; number: number; price: number }>>([]);
  const [lockerAllocations, setLockerAllocations] = useState<Array<{ type: string; number: number; price: number }>>([]);
  const [bicycleAllocations, setBicycleAllocations] = useState<Array<{ type: string; number: number; price: number }>>([]);
  const [loadingParkingTypes, setLoadingParkingTypes] = useState(false);
  const [loadingLockerTypes, setLoadingLockerTypes] = useState(false);
  const [loadingBicycleTypes, setLoadingBicycleTypes] = useState(false);
  const [savingAllocation, setSavingAllocation] = useState(false);

  // Debug: log modal state changes
  useEffect(() => {
    console.log('showParkingModal changed:', showParkingModal);
  }, [showParkingModal]);

  // Unit assign/release
  const [assigningUnit, setAssigningUnit] = useState(false);
  const [assigningUnitLoading, setAssigningUnitLoading] = useState(false);
  const [releasingUnit, setReleasingUnit] = useState(false);
  const [unitSearchQuery, setUnitSearchQuery] = useState('');
  const [unitSearchResults, setUnitSearchResults] = useState<Unit[]>([]);
  const [unitSearchLoading, setUnitSearchLoading] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [releaseReason, setReleaseReason] = useState('');
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);

  // Purchaser management
  const [addingPurchaser, setAddingPurchaser] = useState(false);
  const [purchaserSearchQuery, setPurchaserSearchQuery] = useState('');
  const [purchaserSearchResults, setPurchaserSearchResults] = useState<Contact[]>([]);
  const [purchaserSearchLoading, setPurchaserSearchLoading] = useState(false);
  const [selectedPurchaser, setSelectedPurchaser] = useState<Contact | null>(null);
  const [savingPurchaser, setSavingPurchaser] = useState(false);
  const [deletingPurchaserIndex, setDeletingPurchaserIndex] = useState<number | null>(null);
  const [deletingPurchaserLoading, setDeletingPurchaserLoading] = useState(false);
  const [purchaserMenuOpen, setPurchaserMenuOpen] = useState<number | null>(null);

  const stages = [
    { id: 'NEW', label: 'NEW' },
    { id: 'RESERVED', label: 'RESERVED' },
    { id: 'OUT FOR SIGN', label: 'OUT FOR SIGN' },
    { id: 'SIGNED', label: 'SIGNED' },
    { id: 'CONDITIONAL', label: 'CONDITIONAL' },
    { id: 'FIRM', label: 'FIRM' },
    { id: 'DEFAULT', label: 'DEFAULT' },
    { id: 'RECESSION', label: 'RECESSION' },
    { id: 'UNIT RELEASED', label: 'UNIT RELEASED' }
  ];

  const tabs = [
    { id: 'deal-info', label: 'Deal Info' },
    { id: 'purchasers', label: 'Purchasers' },
    { id: 'purchaser-portal', label: 'Purchaser Portal' },
    { id: 'documents', label: 'Documents' }
  ];

  useEffect(() => {
    if (!isNew && dealId) {
      fetchDeal();
      fetchNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, isNew]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Close purchaser menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (purchaserMenuOpen !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-purchaser-menu]')) {
          setPurchaserMenuOpen(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [purchaserMenuOpen]);

  const fetchDeal = async () => {
    if (!dealId) return;

    try {
      setLoading(true);
      setError('');
      const data = await dealService.getById(workspaceId, dealId);
      setDeal(data.deal);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch deal details');
      console.error('Error fetching deal:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPrimaryPurchaser = (): string => {
    if (!deal) return isNew ? 'New Deal' : '';
    const primary = deal.purchasers.find(p => p.isPrimary);
    return primary?.displayName || deal.purchasers[0]?.displayName || '';
  };

  const getCurrentStage = (): string => {
    if (isNew) return 'NEW';
    return deal?.stage || 'NEW';
  };

  // Dates handlers
  const handleEditDates = () => {
    if (!deal) return;
    // For datetime-local input, we need format: YYYY-MM-DDTHH:mm
    const formatDateTime = (dateStr?: string) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    setDatesData({
      initiatedOn: deal.initiatedOn ? deal.initiatedOn.split('T')[0] : '',
      signedOn: deal.signedOn ? deal.signedOn.split('T')[0] : '',
      executedOn: deal.executedOn ? deal.executedOn.split('T')[0] : '',
      acknowledgementDate: deal.acknowledgementDate ? deal.acknowledgementDate.split('T')[0] : '',
      firmedOn: deal.firmedOn ? deal.firmedOn.split('T')[0] : '',
      extensionDays: deal.extensionDays?.toString() || '',
      coolingExtendedPeriod: deal.coolingExtendedPeriod?.toString() || '',
      reinstateDate: deal.reinstateDate ? deal.reinstateDate.split('T')[0] : '',
      keyPickupDateTime: formatDateTime(deal.keyPickupDateTime),
      reservedOn: deal.reservedOn ? deal.reservedOn.split('T')[0] : '',
      soldOn: deal.soldOn ? deal.soldOn.split('T')[0] : '',
      closedOn: deal.closedOn ? deal.closedOn.split('T')[0] : ''
    });
    setEditingDates(true);
  };

  const handleCancelDates = () => {
    setEditingDates(false);
  };

  const handleDatesChange = (field: string, value: string) => {
    setDatesData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveDates = async () => {
    if (!deal) return;
    try {
      setSavingDates(true);
      const response = await dealService.update(workspaceId, deal._id, {
        initiatedOn: datesData.initiatedOn || undefined,
        signedOn: datesData.signedOn || undefined,
        executedOn: datesData.executedOn || undefined,
        acknowledgementDate: datesData.acknowledgementDate || undefined,
        reservedOn: datesData.reservedOn || undefined,
        firmedOn: datesData.firmedOn || undefined,
        extensionDays: datesData.extensionDays || undefined,
        coolingExtendedPeriod: datesData.coolingExtendedPeriod || undefined,
        reinstateDate: datesData.reinstateDate || undefined,
        keyPickupDateTime: datesData.keyPickupDateTime || undefined,
        soldOn: datesData.soldOn || undefined,
        closedOn: datesData.closedOn || undefined
      });
      setDeal(response.deal);
      setEditingDates(false);
    } catch (err: any) {
      console.error('Error updating dates:', err);
      setError(err.response?.data?.message || 'Failed to update dates');
    } finally {
      setSavingDates(false);
    }
  };

  // Contact search and assignment handlers
  const handleSearchContacts = async (query: string, contactType?: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const filters: any = { search: query };
      if (contactType) {
        filters.contactType = [contactType];
      }
      const response = await contactService.getAll(workspaceId, filters, 1, 10);
      setSearchResults(response.contacts);
    } catch (err: any) {
      console.error('Error searching contacts:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleEditBroker = () => {
    setEditingBroker(true);
    setCurrentContactType('Broker');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedContact(null);
  };

  const handleEditAgent = () => {
    setEditingAgent(true);
    setCurrentContactType('Agent');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedContact(null);
  };

  const handleEditSolicitor = () => {
    setEditingSolicitor(true);
    setCurrentContactType('Solicitor');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedContact(null);
  };

  const handleCancelContactEdit = () => {
    setEditingBroker(false);
    setEditingAgent(false);
    setEditingSolicitor(false);
    setCurrentContactType('');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedContact(null);
  };

  const handleSelectContact = (contact: Contact) => {
    setSelectedContact(contact);
    setSearchResults([]);
    setSearchQuery(''); // Clear search query to prevent triggering another search
  };

  const handleSaveContact = async (type: 'broker' | 'agent' | 'solicitor') => {
    if (!deal || !selectedContact) return;

    try {
      setSavingContact(true);
      const updateData: any = {};

      if (type === 'broker') {
        updateData.brokerId = selectedContact._id;
        updateData.brokerName = selectedContact.displayName || `${selectedContact.firstName} ${selectedContact.lastName}`;
      } else if (type === 'agent') {
        updateData.agentId = selectedContact._id;
        updateData.agentName = selectedContact.displayName || `${selectedContact.firstName} ${selectedContact.lastName}`;
      } else if (type === 'solicitor') {
        updateData.solicitorId = selectedContact._id;
        updateData.solicitorName = selectedContact.displayName || `${selectedContact.firstName} ${selectedContact.lastName}`;
      }

      await dealService.update(workspaceId, deal._id, updateData);

      // Fetch the updated deal with populated contact details
      const updatedDeal = await dealService.getById(workspaceId, deal._id);
      setDeal(updatedDeal.deal);

      handleCancelContactEdit();
    } catch (err: any) {
      console.error(`Error updating ${type}:`, err);
      setError(err.response?.data?.message || `Failed to update ${type}`);
    } finally {
      setSavingContact(false);
    }
  };

  // Third Party handlers
  const handleEditThirdParty = () => {
    if (!deal) return;
    setThirdPartyData({
      firstName: deal.thirdParty?.firstName || '',
      middleName: deal.thirdParty?.middleName || '',
      lastName: deal.thirdParty?.lastName || '',
      displayName: deal.thirdParty?.displayName || '',
      street1: deal.thirdParty?.street1 || '',
      street2: deal.thirdParty?.street2 || '',
      city: deal.thirdParty?.city || '',
      state: deal.thirdParty?.state || '',
      zip: deal.thirdParty?.zip || '',
      fax: deal.thirdParty?.fax || '',
      natureOfBusiness: deal.thirdParty?.natureOfBusiness || ''
    });
    setEditingThirdParty(true);
  };

  const handleSaveThirdParty = async () => {
    if (!deal) return;
    try {
      setSavingThirdParty(true);
      const response = await dealService.update(workspaceId, deal._id, {
        thirdParty: thirdPartyData
      });
      setDeal(response.deal);
      setEditingThirdParty(false);
    } catch (err: any) {
      console.error('Error updating third party:', err);
      setError(err.response?.data?.message || 'Failed to update third party');
    } finally {
      setSavingThirdParty(false);
    }
  };

  const handleCancelThirdParty = () => {
    setEditingThirdParty(false);
  };

  // Conditional Deal handlers
  const handleEditConditionalDeal = () => {
    if (!deal) return;
    setConditionalDealData({
      conditionsRemoved: deal.conditionalDeal?.conditionsRemoved || false,
      conditionsRemovedOn: deal.conditionalDeal?.conditionsRemovedOn ? deal.conditionalDeal.conditionsRemovedOn.split('T')[0] : '',
      conditions: deal.conditionalDeal?.conditions || ''
    });
    setEditingConditionalDeal(true);
  };

  const handleSaveConditionalDeal = async () => {
    if (!deal) return;
    try {
      setSavingConditionalDeal(true);
      const response = await dealService.update(workspaceId, deal._id, {
        conditionalDeal: {
          conditionsRemoved: conditionalDealData.conditionsRemoved,
          conditionsRemovedOn: conditionalDealData.conditionsRemovedOn || undefined,
          conditions: conditionalDealData.conditions
        }
      });
      setDeal(response.deal);
      setEditingConditionalDeal(false);
    } catch (err: any) {
      console.error('Error updating conditional deal:', err);
      setError(err.response?.data?.message || 'Failed to update conditional deal');
    } finally {
      setSavingConditionalDeal(false);
    }
  };

  const handleCancelConditionalDeal = () => {
    setEditingConditionalDeal(false);
  };

  // Corporation handlers
  const handleEditCorporation = () => {
    if (!deal) return;
    setCorporationData({
      name: deal.corporation?.name || '',
      natureOfBusiness: deal.corporation?.natureOfBusiness || '',
      directorNames: deal.corporation?.directorNames || '',
      verified: deal.corporation?.verified || false,
      verifiedSource: deal.corporation?.verifiedSource || '',
      registrationNo: deal.corporation?.registrationNo || '',
      formReceived: deal.corporation?.formReceived || false
    });
    setEditingCorporation(true);
  };

  const handleSaveCorporation = async () => {
    if (!deal) return;
    try {
      setSavingCorporation(true);
      const response = await dealService.update(workspaceId, deal._id, {
        corporation: corporationData
      });
      setDeal(response.deal);
      setEditingCorporation(false);
    } catch (err: any) {
      console.error('Error updating corporation:', err);
      setError(err.response?.data?.message || 'Failed to update corporation');
    } finally {
      setSavingCorporation(false);
    }
  };

  const handleCancelCorporation = () => {
    setEditingCorporation(false);
  };

  // Notes handlers
  const fetchNotes = async () => {
    if (!dealId) return;
    try {
      const fetchedNotes = await noteService.getNotes(workspaceId, 'deal', dealId);
      setNotes(fetchedNotes);
    } catch (err: any) {
      console.error('Error fetching notes:', err);
    }
  };

  const handleAddNote = () => {
    setAddingNote(true);
    setNoteContent('');
  };

  const handleSaveNote = async () => {
    if (!dealId || !noteContent.trim()) return;
    try {
      setSavingNote(true);
      await noteService.createNote(workspaceId, 'deal', dealId, {
        content: noteContent,
        category: 'general'
      });
      await fetchNotes();
      setAddingNote(false);
      setNoteContent('');
      toast.success('Note added successfully');
    } catch (err: any) {
      console.error('Error adding note:', err);
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleCancelNote = () => {
    setAddingNote(false);
    setNoteContent('');
  };

  // Parking/Locker/Bicycle Allocation handlers
  const handleOpenParkingModal = async () => {
    if (!deal) return;
    try {
      setLoadingParkingTypes(true);
      // Fetch parking types for the project
      const response = await api.get(`/workspaces/${workspaceId}/parking-types`, {
        params: { projectId: deal.projectId }
      });
      const parkingTypes = response.data.parkingTypes || [];

      // Get existing allocations
      const existingAllocations = deal.allocateParking || [];

      // Create a map of existing allocations by type
      const existingMap = new Map(existingAllocations.map(a => [a.type, a]));

      // Populate all parking types with existing data or defaults
      const allAllocations = parkingTypes.map((type: any) => {
        const existing = existingMap.get(type.name);
        return {
          type: type.name,
          number: existing?.eligibleNo || 0,
          price: existing?.price || type.price || 0
        };
      });

      setParkingAllocations(allAllocations);
      setShowParkingModal(true);
    } catch (err) {
      console.error('Error fetching parking types:', err);
      toast.error('Failed to load parking types');
    } finally {
      setLoadingParkingTypes(false);
    }
  };

  const handleOpenLockerModal = async () => {
    if (!deal) return;
    try {
      setLoadingLockerTypes(true);
      // Fetch locker types for the project
      const response = await api.get(`/workspaces/${workspaceId}/locker-types`, {
        params: { projectId: deal.projectId }
      });
      const lockerTypes = response.data.lockerTypes || [];

      // Get existing allocations
      const existingAllocations = deal.allocateLocker || [];

      // Create a map of existing allocations by type
      const existingMap = new Map(existingAllocations.map(a => [a.type, a]));

      // Populate all locker types with existing data or defaults
      const allAllocations = lockerTypes.map((type: any) => {
        const existing = existingMap.get(type.name);
        return {
          type: type.name,
          number: existing?.eligibleNo || 0,
          price: existing?.price || type.price || 0
        };
      });

      setLockerAllocations(allAllocations);
      setShowLockerModal(true);
    } catch (err) {
      console.error('Error fetching locker types:', err);
      toast.error('Failed to load locker types');
    } finally {
      setLoadingLockerTypes(false);
    }
  };

  const handleOpenBicycleModal = async () => {
    if (!deal) return;
    try {
      setLoadingBicycleTypes(true);
      // Fetch bicycle types for the project
      const response = await api.get(`/workspaces/${workspaceId}/bicycle-types`, {
        params: { projectId: deal.projectId }
      });
      const bicycleTypes = response.data.bicycleTypes || [];

      // Get existing allocations
      const existingAllocations = deal.allocateBicycle || [];

      // Create a map of existing allocations by type
      const existingMap = new Map(existingAllocations.map(a => [a.type, a]));

      // Populate all bicycle types with existing data or defaults
      const allAllocations = bicycleTypes.map((type: any) => {
        const existing = existingMap.get(type.name);
        return {
          type: type.name,
          number: existing?.eligibleNo || 0,
          price: existing?.price || type.price || 0
        };
      });

      setBicycleAllocations(allAllocations);
      setShowBicycleModal(true);
    } catch (err) {
      console.error('Error fetching bicycle types:', err);
      toast.error('Failed to load bicycle types');
    } finally {
      setLoadingBicycleTypes(false);
    }
  };

  const handleSaveParkingAllocation = async () => {
    if (!deal) return;
    try {
      setSavingAllocation(true);
      const allocateData = parkingAllocations.map(a => ({ type: a.type, eligibleNo: a.number, price: a.price }));
      const response = await dealService.update(workspaceId, deal._id, {
        allocateParking: allocateData
      });
      setDeal(response.deal);
      setShowParkingModal(false);
      toast.success('Parking allocation updated successfully');
    } catch (err: any) {
      console.error('Error updating parking allocation:', err);
      toast.error('Failed to update parking allocation');
    } finally {
      setSavingAllocation(false);
    }
  };

  const handleSaveLockerAllocation = async () => {
    if (!deal) return;
    try {
      setSavingAllocation(true);
      const allocateData = lockerAllocations.map(a => ({ type: a.type, eligibleNo: a.number, price: a.price }));
      const response = await dealService.update(workspaceId, deal._id, {
        allocateLocker: allocateData
      });
      setDeal(response.deal);
      setShowLockerModal(false);
      toast.success('Locker allocation updated successfully');
    } catch (err: any) {
      console.error('Error updating locker allocation:', err);
      toast.error('Failed to update locker allocation');
    } finally {
      setSavingAllocation(false);
    }
  };

  const handleSaveBicycleAllocation = async () => {
    if (!deal) return;
    try {
      setSavingAllocation(true);
      const allocateData = bicycleAllocations.map(a => ({ type: a.type, eligibleNo: a.number, price: a.price }));
      const response = await dealService.update(workspaceId, deal._id, {
        allocateBicycle: allocateData
      });
      setDeal(response.deal);
      setShowBicycleModal(false);
      toast.success('Bicycle allocation updated successfully');
    } catch (err: any) {
      console.error('Error updating bicycle allocation:', err);
      toast.error('Failed to update bicycle allocation');
    } finally {
      setSavingAllocation(false);
    }
  };

  // Unit handlers
  const handleSearchUnits = async (query: string) => {
    if (!query || query.length < 2) {
      setUnitSearchResults([]);
      return;
    }
    try {
      setUnitSearchLoading(true);
      const response = await unitService.getAll(workspaceId, { search: query, status: 'AVAILABLE', page: 1, limit: 10 });
      setUnitSearchResults(response.units);
    } catch (err: any) {
      console.error('Error searching units:', err);
      setUnitSearchResults([]);
    } finally {
      setUnitSearchLoading(false);
    }
  };

  const handleAssignUnit = () => {
    setAssigningUnit(true);
    setUnitSearchQuery('');
    setUnitSearchResults([]);
    setSelectedUnit(null);
  };

  const handleSelectUnit = (unit: Unit) => {
    setSelectedUnit(unit);
    setUnitSearchResults([]);
    setUnitSearchQuery('');
  };

  const handleConfirmAssignUnit = async () => {
    if (!deal || !selectedUnit) return;
    try {
      setAssigningUnitLoading(true);
      const response = await dealService.assignUnit(workspaceId, deal._id, selectedUnit._id);
      setDeal(response.deal);
      setAssigningUnit(false);
      setSelectedUnit(null);
    } catch (err: any) {
      console.error('Error assigning unit:', err);
      setError(err.response?.data?.message || 'Failed to assign unit');
    } finally {
      setAssigningUnitLoading(false);
    }
  };

  const handleCancelAssignUnit = () => {
    setAssigningUnit(false);
    setSelectedUnit(null);
    setUnitSearchQuery('');
    setUnitSearchResults([]);
  };

  const handleReleaseUnit = () => {
    setShowReleaseConfirm(true);
    setReleaseReason('');
  };

  const handleConfirmReleaseUnit = async () => {
    if (!deal) return;
    try {
      setReleasingUnit(true);
      await dealService.releaseUnit(workspaceId, deal._id, releaseReason);

      // Fetch fresh deal data to get populated purchaser details
      const freshDeal = await dealService.getById(workspaceId, deal._id);
      setDeal(freshDeal.deal);
      setShowReleaseConfirm(false);
      setReleaseReason('');
    } catch (err: any) {
      console.error('Error releasing unit:', err);
      setError(err.response?.data?.message || 'Failed to release unit');
    } finally {
      setReleasingUnit(false);
    }
  };

  const handleCancelReleaseUnit = () => {
    setShowReleaseConfirm(false);
    setReleaseReason('');
  };

  // Purchaser management handlers
  const handleAddPurchaser = () => {
    setAddingPurchaser(true);
    setPurchaserSearchQuery('');
    setPurchaserSearchResults([]);
    setSelectedPurchaser(null);
  };

  const handleSearchPurchasers = async (query: string) => {
    if (!query.trim()) {
      setPurchaserSearchResults([]);
      return;
    }

    try {
      setPurchaserSearchLoading(true);
      const response = await contactService.getAll(
        workspaceId,
        {
          search: query,
          contactType: ['End-User']
        },
        1,
        10
      );
      setPurchaserSearchResults(response.contacts || []);
    } catch (err: any) {
      console.error('Error searching purchasers:', err);
      setPurchaserSearchResults([]);
    } finally {
      setPurchaserSearchLoading(false);
    }
  };

  const handleSelectPurchaser = (contact: Contact) => {
    setSelectedPurchaser(contact);
    setPurchaserSearchResults([]);
    setPurchaserSearchQuery('');
  };

  const handleConfirmAddPurchaser = async () => {
    if (!deal || !selectedPurchaser) return;

    try {
      setSavingPurchaser(true);

      // Only save contactId and isPrimary
      const newPurchaser = {
        contactId: selectedPurchaser._id,
        isPrimary: !deal.purchasers || deal.purchasers.length === 0
      };

      const updatedPurchasers = [...(deal.purchasers || []), newPurchaser];
      await dealService.update(workspaceId, deal._id, { purchasers: updatedPurchasers });

      // Fetch fresh deal data to get populated purchaser details
      const freshDeal = await dealService.getById(workspaceId, deal._id);
      setDeal(freshDeal.deal);
      setAddingPurchaser(false);
      setSelectedPurchaser(null);

      toast.success(`${selectedPurchaser.displayName} has been added successfully`);
    } catch (err: any) {
      console.error('Error adding purchaser:', err);
      setError(err.response?.data?.message || 'Failed to add purchaser');
      toast.error('Failed to add purchaser');
    } finally {
      setSavingPurchaser(false);
    }
  };

  const handleCancelAddPurchaser = () => {
    setAddingPurchaser(false);
    setSelectedPurchaser(null);
    setPurchaserSearchQuery('');
    setPurchaserSearchResults([]);
  };

  const handleDeletePurchaser = (index: number) => {
    if (!deal || !deal.purchasers) return;

    // Check if this is the primary purchaser
    if (deal.purchasers[index]?.isPrimary) {
      toast.warning('Primary purchaser cannot be deleted. Please set another purchaser as primary first.');
      return;
    }

    setDeletingPurchaserIndex(index);
  };

  const handleConfirmDeletePurchaser = async () => {
    if (!deal || !deal.purchasers || deletingPurchaserIndex === null) return;

    try {
      setDeletingPurchaserLoading(true);
      const purchaserName = deal.purchasers[deletingPurchaserIndex]?.displayName ||
                           `${deal.purchasers[deletingPurchaserIndex]?.firstName} ${deal.purchasers[deletingPurchaserIndex]?.lastName}`;

      // Only keep contactId and isPrimary when updating
      const updatedPurchasers = deal.purchasers
        .filter((_, i) => i !== deletingPurchaserIndex)
        .map(p => ({ contactId: p.contactId, isPrimary: p.isPrimary }));

      await dealService.update(workspaceId, deal._id, { purchasers: updatedPurchasers });

      // Fetch fresh deal data to get populated purchaser details
      const freshDeal = await dealService.getById(workspaceId, deal._id);
      setDeal(freshDeal.deal);
      setDeletingPurchaserIndex(null);

      toast.success(`${purchaserName} has been removed successfully`);
    } catch (err: any) {
      console.error('Error deleting purchaser:', err);
      setError(err.response?.data?.message || 'Failed to delete purchaser');
      toast.error('Failed to remove purchaser');
    } finally {
      setDeletingPurchaserLoading(false);
    }
  };

  const handleCancelDeletePurchaser = () => {
    setDeletingPurchaserIndex(null);
  };

  const handleSetPrimaryPurchaser = async (index: number) => {
    if (!deal || !deal.purchasers) return;

    try {
      const purchaserName = deal.purchasers[index]?.displayName ||
                           `${deal.purchasers[index]?.firstName} ${deal.purchasers[index]?.lastName}`;

      // Only keep contactId and isPrimary when updating
      const updatedPurchasers = deal.purchasers.map((p, i) => ({
        contactId: p.contactId,
        isPrimary: i === index
      }));

      await dealService.update(workspaceId, deal._id, { purchasers: updatedPurchasers });

      // Fetch fresh deal data to get populated purchaser details
      const freshDeal = await dealService.getById(workspaceId, deal._id);
      setDeal(freshDeal.deal);

      toast.success(`${purchaserName} is now the primary purchaser`);
    } catch (err: any) {
      console.error('Error setting primary purchaser:', err);
      setError(err.response?.data?.message || 'Failed to set primary purchaser');
      toast.error('Failed to set primary purchaser');
    }
  };

  // Debounce search for contacts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        handleSearchContacts(searchQuery, currentContactType);
      }
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, currentContactType]);

  // Debounce search for units
  useEffect(() => {
    const timer = setTimeout(() => {
      if (unitSearchQuery) {
        handleSearchUnits(unitSearchQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitSearchQuery]);

  // Debounce search for purchasers
  useEffect(() => {
    const timer = setTimeout(() => {
      if (purchaserSearchQuery) {
        handleSearchPurchasers(purchaserSearchQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaserSearchQuery]);

  if (loading) {
    return (
      <>
        <div className="deal-details-overlay" onClick={onClose} />
        <div className="deal-details-slider">
          <div className="deal-details-loading">
            <div className="deal-details-loading-spinner" />
          </div>
        </div>
      </>
    );
  }

  if (!isNew && (error || !deal)) {
    return (
      <>
        <div className="deal-details-overlay" onClick={onClose} />
        <div className="deal-details-slider">
          <div className="deal-details-error">
            <p>{error || 'Deal not found'}</p>
            <button className="btn btn-secondary btn-md" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="deal-details-overlay" onClick={onClose} />
      <div className="deal-details-slider">
        {/* Header */}
        <div className="deal-details-header">
          <button className="deal-details-back-btn" onClick={onClose}>
            ← Back
          </button>
          <div className="deal-details-header-title">
            <span className="deal-details-header-label">Deal :</span>
            <span className="deal-details-header-purchaser">{getPrimaryPurchaser()}</span>
            {deal && (
              <span className="deal-details-header-unit">{deal.projectName} {deal.unitNo}</span>
            )}
          </div>
          <button
            className="deal-details-close-btn"
            onClick={onClose}
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Workflow Stages */}
        <div className="deal-details-stages-container">
          <div className="deal-details-stages">
            {stages.map((stage, index) => (
              <div
                key={stage.id}
                className={`deal-details-stage ${getCurrentStage() === stage.id ? 'active' : ''}`}
              >
                {stage.label}
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="deal-details-tabs-container">
          <div className="deal-details-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`deal-details-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="deal-details-content">
          {activeTab === 'deal-info' && (
            <div className="deal-details-tab-content">
              {/* Project/Unit Tile */}
              {deal && (
                <div className="deal-info-tile">
                  <h3 className="deal-info-tile-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Project/Unit
                    {deal.unitId ? (
                      <button
                        className="deal-info-edit-btn"
                        onClick={handleReleaseUnit}
                        style={{ marginLeft: 'auto', background: '#DC2626', color: 'white', padding: '6px 12px', borderRadius: '4px' }}
                      >
                        Release Unit
                      </button>
                    ) : (
                      <button
                        className="deal-info-edit-btn"
                        onClick={handleAssignUnit}
                        style={{ marginLeft: 'auto', background: '#48182F', color: 'white', padding: '6px 12px', borderRadius: '4px' }}
                      >
                        Assign Unit
                      </button>
                    )}
                  </h3>
                  {assigningUnit ? (
                    <>
                      <div className="deal-info-grid" style={{ padding: '16px' }}>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Search units..."
                            value={unitSearchQuery}
                            onChange={(e) => setUnitSearchQuery(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              fontSize: '14px',
                              border: '1px solid #D4CBC4',
                              borderRadius: '8px',
                              backgroundColor: '#FFFFFF',
                              color: '#48182F'
                            }}
                          />
                          {unitSearchLoading && (
                            <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                              <div className="spinner-small" />
                            </div>
                          )}
                          {unitSearchResults.length > 0 && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              maxHeight: '200px',
                              overflowY: 'auto',
                              background: 'white',
                              border: '1px solid #D4CBC4',
                              borderRadius: '8px',
                              marginTop: '4px',
                              zIndex: 10,
                              boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15)'
                            }}>
                              {unitSearchResults.map((unit) => (
                                <div
                                  key={unit._id}
                                  onClick={() => handleSelectUnit(unit)}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #F5F3F0',
                                    fontSize: '13px'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#F5F3F0'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                >
                                  <div style={{ fontWeight: 600, color: '#48182F' }}>{unit.unitNo}</div>
                                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                    {unit.projectName} - {unit.modelName}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {selectedUnit && (
                          <div style={{
                            marginTop: '16px',
                            padding: '16px',
                            background: '#F5F3F0',
                            borderRadius: '8px',
                            border: '1px solid #D4CBC4'
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: '6px', color: '#48182F', fontSize: '14px' }}>
                              Selected: {selectedUnit.unitNo}
                            </div>
                            <div style={{ fontSize: '13px', color: '#6B7280' }}>
                              {selectedUnit.projectName} - {selectedUnit.modelName}
                            </div>
                          </div>
                        )}
                      </div>
                      <div style={{
                        padding: '16px',
                        borderTop: '1px solid #D4CBC4',
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'flex-end'
                      }}>
                        <button
                          onClick={handleCancelAssignUnit}
                          style={{
                            padding: '10px 20px',
                            fontSize: '14px',
                            border: 'none',
                            borderRadius: '8px',
                            background: '#D4CBC4',
                            color: '#48182F',
                            cursor: 'pointer',
                            fontWeight: 600,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#C4B9B1';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#D4CBC4';
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleConfirmAssignUnit}
                          disabled={!selectedUnit || assigningUnitLoading}
                          style={{
                            padding: '10px 20px',
                            fontSize: '14px',
                            border: 'none',
                            borderRadius: '8px',
                            background: (selectedUnit && !assigningUnitLoading) ? '#48182F' : '#9CA3AF',
                            color: 'white',
                            cursor: (selectedUnit && !assigningUnitLoading) ? 'pointer' : 'not-allowed',
                            opacity: assigningUnitLoading ? 0.7 : 1,
                            fontWeight: 600,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (selectedUnit && !assigningUnitLoading) {
                              e.currentTarget.style.background = '#5A1E3A';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedUnit && !assigningUnitLoading) {
                              e.currentTarget.style.background = '#48182F';
                            }
                          }}
                        >
                          {assigningUnitLoading ? 'Assigning...' : 'Assign Unit'}
                        </button>
                      </div>
                    </>
                  ) : !deal.unitId ? (
                    <p className="deal-info-empty">No unit assigned. Click "Assign Unit" to assign a unit.</p>
                  ) : (
                    <div className="deal-info-grid">
                    <div className="deal-info-row">
                      <span className="deal-info-label">Project name</span>
                      <span className="deal-info-value">: {deal.projectName || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Builder</span>
                      <span className="deal-info-value">: {deal.builderName || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Model</span>
                      <span className="deal-info-value">: {deal.modelName || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Unit</span>
                      <span className="deal-info-value">: {deal.unitDetails?.unitNo || deal.unitNo || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Marketing Unit</span>
                      <span className="deal-info-value">: {deal.unitDetails?.unitNoMarketing || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Legal Floor</span>
                      <span className="deal-info-value">: {deal.unitDetails?.floorLegal || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Marketing Floor</span>
                      <span className="deal-info-value">: {deal.unitDetails?.floorMarketing || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Legal Unit</span>
                      <span className="deal-info-value">: {deal.unitDetails?.unitNoLegal || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Collection</span>
                      <span className="deal-info-value">: {deal.unitDetails?.modelCollection || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Bathroom</span>
                      <span className="deal-info-value">: {deal.unitDetails?.bath || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Bedroom</span>
                      <span className="deal-info-value">: {deal.unitDetails?.bed || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Den</span>
                      <span className="deal-info-value">: {deal.unitDetails?.den || ''}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Media</span>
                      <span className="deal-info-value">: {deal.unitDetails?.media || ''}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Flex</span>
                      <span className="deal-info-value">: {deal.unitDetails?.flex || ''}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Area (Sq.Ft)</span>
                      <span className="deal-info-value">: {deal.unitDetails?.area || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Ceiling (Ft.)</span>
                      <span className="deal-info-value">: {deal.unitDetails?.ceiling || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Outdoor Type</span>
                      <span className="deal-info-value">: {deal.unitDetails?.outdoorType || ''}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Outdoor Area (Sq.Ft)</span>
                      <span className="deal-info-value">: {deal.unitDetails?.outdoorArea || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">View</span>
                      <span className="deal-info-value">: {deal.unitDetails?.views?.join(', ') || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Purchaser Type</span>
                      <span className="deal-info-value">: {deal.unitDetails?.type || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Right To Lease</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Free Assignment</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Window Coverings</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Deposit Change</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Sale Source</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Is waiting List Storage</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Is waiting List Parking</span>
                      <span className="deal-info-value">: </span>
                    </div>
                  </div>
                  )}
                </div>
              )}
              {!deal && (
                <div className="deal-info-tile">
                  <h3 className="deal-info-tile-title">Project/Unit</h3>
                  <p className="deal-info-empty">No unit assigned yet</p>
                </div>
              )}

              {/* Finance Tile */}
              <div className="deal-info-tile">
                <h3 className="deal-info-tile-title">Finance</h3>
                {deal ? (
                  <div className="deal-info-grid">
                    <div className="deal-info-row">
                      <span className="deal-info-label">Base Price</span>
                      <span className="deal-info-value">: ${deal.finance.basePrice?.toLocaleString() || '0.00'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Price / (Sq.Ft)</span>
                      <span className="deal-info-value">: ${deal.finance.priceSqft?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Floor Premium</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Unit Premium</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">NET Price</span>
                      <span className="deal-info-value">: ${deal.finance.salesPrice?.toLocaleString() || '0.00'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Sale Price</span>
                      <span className="deal-info-value">: ${deal.finance.salesPrice?.toLocaleString() || '0.00'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Total Discounts</span>
                      <span className="deal-info-value">: $0.00</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Contract Price</span>
                      <span className="deal-info-value">: $0.00</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Tax</span>
                      <span className="deal-info-value">: ${deal.finance.tax?.toLocaleString() || '0.00'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Parking Price</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Locker Price</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Bicycle Price</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Total NSF Fee Paid</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">NSF Count</span>
                      <span className="deal-info-value">: </span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Total Penalty</span>
                      <span className="deal-info-value">: </span>
                    </div>
                  </div>
                ) : (
                  <p className="deal-info-empty">No finance information</p>
                )}
              </div>

              {/* Dates Tile */}
              <div className="deal-info-tile">
                <h3 className="deal-info-tile-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Dates
                  {!editingDates && (
                    <button className="deal-info-edit-btn" onClick={handleEditDates} style={{ marginLeft: 'auto' }}>
                      <Pencil size={16} />
                    </button>
                  )}
                </h3>
                {deal ? (
                  editingDates ? (
                    <>
                      <div className="deal-info-grid">
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Initiated On</label>
                          <input
                            type="date"
                            className="form-input"
                            value={datesData.initiatedOn}
                            onChange={(e) => handleDatesChange('initiatedOn', e.target.value)}
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Signed On</label>
                          <input
                            type="date"
                            className="form-input"
                            value={datesData.signedOn}
                            onChange={(e) => handleDatesChange('signedOn', e.target.value)}
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Executed On</label>
                          <input
                            type="date"
                            className="form-input"
                            value={datesData.executedOn}
                            onChange={(e) => handleDatesChange('executedOn', e.target.value)}
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Acknowledgement Date</label>
                          <input
                            type="date"
                            className="form-input"
                            value={datesData.acknowledgementDate}
                            onChange={(e) => handleDatesChange('acknowledgementDate', e.target.value)}
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Firm On</label>
                          <input
                            type="date"
                            className="form-input"
                            value={datesData.firmedOn}
                            onChange={(e) => handleDatesChange('firmedOn', e.target.value)}
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Reserved On</label>
                          <input
                            type="date"
                            className="form-input"
                            value={datesData.reservedOn}
                            onChange={(e) => handleDatesChange('reservedOn', e.target.value)}
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Sold On</label>
                          <input
                            type="date"
                            className="form-input"
                            value={datesData.soldOn}
                            onChange={(e) => handleDatesChange('soldOn', e.target.value)}
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Closed On</label>
                          <input
                            type="date"
                            className="form-input"
                            value={datesData.closedOn}
                            onChange={(e) => handleDatesChange('closedOn', e.target.value)}
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Extension Days</label>
                          <input
                            type="text"
                            className="form-input"
                            value={datesData.extensionDays}
                            onChange={(e) => handleDatesChange('extensionDays', e.target.value)}
                            placeholder="Enter number of days"
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Cooling Extended Period</label>
                          <input
                            type="text"
                            className="form-input"
                            value={datesData.coolingExtendedPeriod}
                            onChange={(e) => handleDatesChange('coolingExtendedPeriod', e.target.value)}
                            placeholder="Enter cooling period"
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Reinstate Date</label>
                          <input
                            type="date"
                            className="form-input"
                            value={datesData.reinstateDate}
                            onChange={(e) => handleDatesChange('reinstateDate', e.target.value)}
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                        <div className="deal-info-row" style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: 'none' }}>
                          <label style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>Key Pickup Date & Time</label>
                          <input
                            type="datetime-local"
                            className="form-input"
                            value={datesData.keyPickupDateTime}
                            onChange={(e) => handleDatesChange('keyPickupDateTime', e.target.value)}
                            style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                          />
                        </div>
                      </div>
                      <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end', padding: '0 16px 12px' }}>
                        <button
                          className="btn btn-secondary btn-md"
                          onClick={handleCancelDates}
                          disabled={savingDates}
                          style={{ padding: '8px 16px', fontSize: '13px' }}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-primary btn-md"
                          onClick={handleSaveDates}
                          disabled={savingDates}
                          style={{ padding: '8px 16px', fontSize: '13px' }}
                        >
                          {savingDates ? 'Updating...' : 'Update'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="deal-info-grid">
                      <div className="deal-info-row">
                        <span className="deal-info-label">Initiated On</span>
                        <span className="deal-info-value">: {deal.initiatedOn ? new Date(deal.initiatedOn).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Signed On</span>
                        <span className="deal-info-value">: {deal.signedOn ? new Date(deal.signedOn).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Executed On</span>
                        <span className="deal-info-value">: {deal.executedOn ? new Date(deal.executedOn).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Acknowledgement Date</span>
                        <span className="deal-info-value">: {deal.acknowledgementDate ? new Date(deal.acknowledgementDate).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Firm On</span>
                        <span className="deal-info-value">: {deal.firmedOn ? new Date(deal.firmedOn).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Reserved On</span>
                        <span className="deal-info-value">: {deal.reservedOn ? new Date(deal.reservedOn).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Sold On</span>
                        <span className="deal-info-value">: {deal.soldOn ? new Date(deal.soldOn).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Closed On</span>
                        <span className="deal-info-value">: {deal.closedOn ? new Date(deal.closedOn).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Extension Days</span>
                        <span className="deal-info-value">: {deal.extensionDays ?? ''}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Cooling Extended Period</span>
                        <span className="deal-info-value">: {deal.coolingExtendedPeriod ?? ''}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Reinstate Date</span>
                        <span className="deal-info-value">: {deal.reinstateDate ? new Date(deal.reinstateDate).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Key Pickup Date & Time</span>
                        <span className="deal-info-value">: {deal.keyPickupDateTime ? new Date(deal.keyPickupDateTime).toLocaleString() : ''}</span>
                      </div>
                    </div>
                  )
                ) : (
                  <p className="deal-info-empty">No date information</p>
                )}
              </div>

              {/* Broker Tile */}
              <div className="deal-info-tile">
                <h3 className="deal-info-tile-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Broker
                  {!editingBroker && (
                    <button className="deal-info-edit-btn" onClick={handleEditBroker} style={{ marginLeft: 'auto' }}>
                      <Pencil size={16} />
                    </button>
                  )}
                </h3>
                {editingBroker ? (
                  <>
                    <div className="deal-info-grid" style={{ padding: '16px' }}>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Search contacts..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {searchLoading && (
                          <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                            <div className="spinner-small" />
                          </div>
                        )}
                        {searchResults.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #D1D5DB', borderRadius: '6px', marginTop: '4px', maxHeight: '200px', overflowY: 'auto', zIndex: 10, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                            {searchResults.map((contact) => (
                              <div
                                key={contact._id}
                                onClick={() => handleSelectContact(contact)}
                                style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', fontSize: '13px' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                              >
                                <div style={{ fontWeight: 500 }}>{contact.displayName || `${contact.firstName} ${contact.lastName}`}</div>
                                <div style={{ fontSize: '12px', color: '#6B7280' }}>{contact.emails?.[0]?.email || ''}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {selectedContact && (
                        <div style={{ marginTop: '12px', padding: '12px', background: '#F9FAFB', borderRadius: '6px', fontSize: '13px' }}>
                          <div style={{ fontWeight: 500, marginBottom: '4px' }}>Selected: {selectedContact.displayName || `${selectedContact.firstName} ${selectedContact.lastName}`}</div>
                          <div style={{ color: '#6B7280' }}>{selectedContact.emails?.[0]?.email || ''}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', padding: '0 16px 12px' }}>
                      <button
                        className="btn btn-secondary btn-md"
                        onClick={handleCancelContactEdit}
                        disabled={savingContact}
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary btn-md"
                        onClick={() => handleSaveContact('broker')}
                        disabled={savingContact || !selectedContact}
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                      >
                        {savingContact ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  deal?.brokerId ? (
                    <div className="deal-info-grid">
                      <div className="deal-info-row">
                        <span className="deal-info-label">Broker Name</span>
                        <span className="deal-info-value">
                          : {deal.broker?.displayName || deal.brokerName || '—'}
                          {deal.brokerId && (
                            <a
                              href={`/workspaces/${workspaceId}/contacts?view=${deal.brokerId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ marginLeft: '8px', color: '#3B82F6', textDecoration: 'none', fontSize: '12px' }}
                              title="View contact details"
                            >
                              (View Details)
                            </a>
                          )}
                        </span>
                      </div>
                      {deal.broker?.emails?.[0]?.email && (
                        <div className="deal-info-row">
                          <span className="deal-info-label">Email</span>
                          <span className="deal-info-value">
                            : <a href={`mailto:${deal.broker.emails[0].email}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>
                              {deal.broker.emails[0].email}
                            </a>
                          </span>
                        </div>
                      )}
                      {deal.broker?.phones?.[0]?.number && (
                        <div className="deal-info-row">
                          <span className="deal-info-label">Phone</span>
                          <span className="deal-info-value">
                            : <a href={`tel:${deal.broker.phones[0].number}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>
                              {deal.broker.phones[0].number}
                            </a>
                          </span>
                        </div>
                      )}
                      <div className="deal-info-row">
                        <span className="deal-info-label">Commission</span>
                        <span className="deal-info-value">: {deal.commission ? `$${deal.commission.toLocaleString()}` : '—'}</span>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Commission %</span>
                        <span className="deal-info-value">: {deal.commissionPercent ? `${deal.commissionPercent}%` : '—'}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="deal-info-empty">No broker assigned</p>
                  )
                )}
              </div>

              {/* Sales Agent Tile */}
              <div className="deal-info-tile">
                <h3 className="deal-info-tile-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Sales Agent
                  {!editingAgent && (
                    <button className="deal-info-edit-btn" onClick={handleEditAgent} style={{ marginLeft: 'auto' }}>
                      <Pencil size={16} />
                    </button>
                  )}
                </h3>
                {editingAgent ? (
                  <>
                    <div className="deal-info-grid" style={{ padding: '16px' }}>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Search contacts..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {searchLoading && (
                          <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                            <div className="spinner-small" />
                          </div>
                        )}
                        {searchResults.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #D1D5DB', borderRadius: '6px', marginTop: '4px', maxHeight: '200px', overflowY: 'auto', zIndex: 10, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                            {searchResults.map((contact) => (
                              <div
                                key={contact._id}
                                onClick={() => handleSelectContact(contact)}
                                style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', fontSize: '13px' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                              >
                                <div style={{ fontWeight: 500 }}>{contact.displayName || `${contact.firstName} ${contact.lastName}`}</div>
                                <div style={{ fontSize: '12px', color: '#6B7280' }}>{contact.emails?.[0]?.email || ''}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {selectedContact && (
                        <div style={{ marginTop: '12px', padding: '12px', background: '#F9FAFB', borderRadius: '6px', fontSize: '13px' }}>
                          <div style={{ fontWeight: 500, marginBottom: '4px' }}>Selected: {selectedContact.displayName || `${selectedContact.firstName} ${selectedContact.lastName}`}</div>
                          <div style={{ color: '#6B7280' }}>{selectedContact.emails?.[0]?.email || ''}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', padding: '0 16px 12px' }}>
                      <button
                        className="btn btn-secondary btn-md"
                        onClick={handleCancelContactEdit}
                        disabled={savingContact}
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary btn-md"
                        onClick={() => handleSaveContact('agent')}
                        disabled={savingContact || !selectedContact}
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                      >
                        {savingContact ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  deal?.agentId ? (
                    <div className="deal-info-grid">
                      <div className="deal-info-row">
                        <span className="deal-info-label">Agent Name</span>
                        <span className="deal-info-value">
                          : {deal.agent?.displayName || deal.agentName || '—'}
                          {deal.agentId && (
                            <a
                              href={`/workspaces/${workspaceId}/contacts?view=${deal.agentId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ marginLeft: '8px', color: '#3B82F6', textDecoration: 'none', fontSize: '12px' }}
                              title="View contact details"
                            >
                              (View Details)
                            </a>
                          )}
                        </span>
                      </div>
                      {deal.agent?.emails?.[0]?.email && (
                        <div className="deal-info-row">
                          <span className="deal-info-label">Email</span>
                          <span className="deal-info-value">
                            : <a href={`mailto:${deal.agent.emails[0].email}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>
                              {deal.agent.emails[0].email}
                            </a>
                          </span>
                        </div>
                      )}
                      {deal.agent?.phones?.[0]?.number && (
                        <div className="deal-info-row">
                          <span className="deal-info-label">Phone</span>
                          <span className="deal-info-value">
                            : <a href={`tel:${deal.agent.phones[0].number}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>
                              {deal.agent.phones[0].number}
                            </a>
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="deal-info-empty">No sales agent assigned</p>
                  )
                )}
              </div>

              {/* Solicitor Tile */}
              <div className="deal-info-tile">
                <h3 className="deal-info-tile-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Solicitor
                  {!editingSolicitor && (
                    <button className="deal-info-edit-btn" onClick={handleEditSolicitor} style={{ marginLeft: 'auto' }}>
                      <Pencil size={16} />
                    </button>
                  )}
                </h3>
                {editingSolicitor ? (
                  <>
                    <div className="deal-info-grid" style={{ padding: '16px' }}>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Search contacts..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                        />
                        {searchLoading && (
                          <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                            <div className="spinner-small" />
                          </div>
                        )}
                        {searchResults.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #D1D5DB', borderRadius: '6px', marginTop: '4px', maxHeight: '200px', overflowY: 'auto', zIndex: 10, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                            {searchResults.map((contact) => (
                              <div
                                key={contact._id}
                                onClick={() => handleSelectContact(contact)}
                                style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', fontSize: '13px' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                              >
                                <div style={{ fontWeight: 500 }}>{contact.displayName || `${contact.firstName} ${contact.lastName}`}</div>
                                <div style={{ fontSize: '12px', color: '#6B7280' }}>{contact.emails?.[0]?.email || ''}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {selectedContact && (
                        <div style={{ marginTop: '12px', padding: '12px', background: '#F9FAFB', borderRadius: '6px', fontSize: '13px' }}>
                          <div style={{ fontWeight: 500, marginBottom: '4px' }}>Selected: {selectedContact.displayName || `${selectedContact.firstName} ${selectedContact.lastName}`}</div>
                          <div style={{ color: '#6B7280' }}>{selectedContact.emails?.[0]?.email || ''}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', padding: '0 16px 12px' }}>
                      <button
                        className="btn btn-secondary btn-md"
                        onClick={handleCancelContactEdit}
                        disabled={savingContact}
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary btn-md"
                        onClick={() => handleSaveContact('solicitor')}
                        disabled={savingContact || !selectedContact}
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                      >
                        {savingContact ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  deal?.solicitorId ? (
                    <div className="deal-info-grid">
                      <div className="deal-info-row">
                        <span className="deal-info-label">Solicitor Name</span>
                        <span className="deal-info-value">
                          : {deal.solicitor?.displayName || deal.solicitorName || '—'}
                          {deal.solicitorId && (
                            <a
                              href={`/workspaces/${workspaceId}/contacts?view=${deal.solicitorId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ marginLeft: '8px', color: '#3B82F6', textDecoration: 'none', fontSize: '12px' }}
                              title="View contact details"
                            >
                              (View Details)
                            </a>
                          )}
                        </span>
                      </div>
                      {deal.solicitor?.emails?.[0]?.email && (
                        <div className="deal-info-row">
                          <span className="deal-info-label">Email</span>
                          <span className="deal-info-value">
                            : <a href={`mailto:${deal.solicitor.emails[0].email}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>
                              {deal.solicitor.emails[0].email}
                            </a>
                          </span>
                        </div>
                      )}
                      {deal.solicitor?.phones?.[0]?.number && (
                        <div className="deal-info-row">
                          <span className="deal-info-label">Phone</span>
                          <span className="deal-info-value">
                            : <a href={`tel:${deal.solicitor.phones[0].number}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>
                              {deal.solicitor.phones[0].number}
                            </a>
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="deal-info-empty">No solicitor assigned</p>
                  )
                )}
              </div>

              {/* Third Party Tile */}
              <div className="deal-info-tile">
                <h3 className="deal-info-tile-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Third Party
                  {!editingThirdParty && (
                    <button className="deal-info-edit-btn" onClick={handleEditThirdParty} style={{ marginLeft: 'auto' }}>
                      <Pencil size={16} />
                    </button>
                  )}
                </h3>
                {editingThirdParty ? (
                  <>
                    <div className="deal-info-grid" style={{ padding: '16px' }}>
                      <div className="deal-info-row">
                        <span className="deal-info-label">First name</span>
                        <input
                          type="text"
                          value={thirdPartyData.firstName}
                          onChange={(e) => setThirdPartyData({ ...thirdPartyData, firstName: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Middle Name</span>
                        <input
                          type="text"
                          value={thirdPartyData.middleName}
                          onChange={(e) => setThirdPartyData({ ...thirdPartyData, middleName: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Last name</span>
                        <input
                          type="text"
                          value={thirdPartyData.lastName}
                          onChange={(e) => setThirdPartyData({ ...thirdPartyData, lastName: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Display name</span>
                        <input
                          type="text"
                          value={thirdPartyData.displayName}
                          onChange={(e) => setThirdPartyData({ ...thirdPartyData, displayName: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Street 1</span>
                        <input
                          type="text"
                          value={thirdPartyData.street1}
                          onChange={(e) => setThirdPartyData({ ...thirdPartyData, street1: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Street 2</span>
                        <input
                          type="text"
                          value={thirdPartyData.street2}
                          onChange={(e) => setThirdPartyData({ ...thirdPartyData, street2: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">City</span>
                        <input
                          type="text"
                          value={thirdPartyData.city}
                          onChange={(e) => setThirdPartyData({ ...thirdPartyData, city: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">State</span>
                        <input
                          type="text"
                          value={thirdPartyData.state}
                          onChange={(e) => setThirdPartyData({ ...thirdPartyData, state: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Zip</span>
                        <input
                          type="text"
                          value={thirdPartyData.zip}
                          onChange={(e) => setThirdPartyData({ ...thirdPartyData, zip: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Fax</span>
                        <input
                          type="text"
                          value={thirdPartyData.fax}
                          onChange={(e) => setThirdPartyData({ ...thirdPartyData, fax: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Nature of Business</span>
                        <input
                          type="text"
                          value={thirdPartyData.natureOfBusiness}
                          onChange={(e) => setThirdPartyData({ ...thirdPartyData, natureOfBusiness: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                    </div>
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={handleCancelThirdParty}
                        style={{ padding: '8px 16px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveThirdParty}
                        disabled={savingThirdParty}
                        style={{ padding: '8px 16px', fontSize: '13px', border: 'none', borderRadius: '6px', background: '#48182F', color: 'white', cursor: 'pointer' }}
                      >
                        {savingThirdParty ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="deal-info-grid">
                    <div className="deal-info-row">
                      <span className="deal-info-label">First name</span>
                      <span className="deal-info-value">: {deal?.thirdParty?.firstName || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Middle Name</span>
                      <span className="deal-info-value">: {deal?.thirdParty?.middleName || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Last name</span>
                      <span className="deal-info-value">: {deal?.thirdParty?.lastName || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Display name</span>
                      <span className="deal-info-value">: {deal?.thirdParty?.displayName || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Street 1</span>
                      <span className="deal-info-value">: {deal?.thirdParty?.street1 || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Street 2</span>
                      <span className="deal-info-value">: {deal?.thirdParty?.street2 || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">City</span>
                      <span className="deal-info-value">: {deal?.thirdParty?.city || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">State</span>
                      <span className="deal-info-value">: {deal?.thirdParty?.state || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Zip</span>
                      <span className="deal-info-value">: {deal?.thirdParty?.zip || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Fax</span>
                      <span className="deal-info-value">: {deal?.thirdParty?.fax || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Nature of Business</span>
                      <span className="deal-info-value">: {deal?.thirdParty?.natureOfBusiness || '—'}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Conditional Deal Tile */}
              <div className="deal-info-tile">
                <h3 className="deal-info-tile-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Conditional Deal
                  {!editingConditionalDeal && (
                    <button className="deal-info-edit-btn" onClick={handleEditConditionalDeal} style={{ marginLeft: 'auto' }}>
                      <Pencil size={16} />
                    </button>
                  )}
                </h3>
                {editingConditionalDeal ? (
                  <>
                    <div className="deal-info-grid" style={{ padding: '16px' }}>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Conditions Removed</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            checked={conditionalDealData.conditionsRemoved}
                            onChange={(e) => setConditionalDealData({ ...conditionalDealData, conditionsRemoved: e.target.checked })}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <span style={{ fontSize: '13px' }}>{conditionalDealData.conditionsRemoved ? 'Yes' : 'No'}</span>
                        </label>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Conditions Removed On</span>
                        <input
                          type="date"
                          value={conditionalDealData.conditionsRemovedOn}
                          onChange={(e) => setConditionalDealData({ ...conditionalDealData, conditionsRemovedOn: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Conditions</span>
                        <textarea
                          value={conditionalDealData.conditions}
                          onChange={(e) => setConditionalDealData({ ...conditionalDealData, conditions: e.target.value })}
                          rows={3}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px', resize: 'vertical' }}
                        />
                      </div>
                    </div>
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={handleCancelConditionalDeal}
                        style={{ padding: '8px 16px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveConditionalDeal}
                        disabled={savingConditionalDeal}
                        style={{ padding: '8px 16px', fontSize: '13px', border: 'none', borderRadius: '6px', background: '#48182F', color: 'white', cursor: 'pointer' }}
                      >
                        {savingConditionalDeal ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="deal-info-grid">
                    <div className="deal-info-row">
                      <span className="deal-info-label">Conditions Removed</span>
                      <span className="deal-info-value">: {deal?.conditionalDeal?.conditionsRemoved ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Conditions Removed On</span>
                      <span className="deal-info-value">: {deal?.conditionalDeal?.conditionsRemovedOn ? new Date(deal.conditionalDeal.conditionsRemovedOn).toLocaleDateString() : '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Conditions</span>
                      <span className="deal-info-value">: {deal?.conditionalDeal?.conditions || '—'}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Corporation Tile */}
              <div className="deal-info-tile">
                <h3 className="deal-info-tile-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Corporation
                  {!editingCorporation && (
                    <button className="deal-info-edit-btn" onClick={handleEditCorporation} style={{ marginLeft: 'auto' }}>
                      <Pencil size={16} />
                    </button>
                  )}
                </h3>
                {editingCorporation ? (
                  <>
                    <div className="deal-info-grid" style={{ padding: '16px' }}>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Name</span>
                        <input
                          type="text"
                          value={corporationData.name}
                          onChange={(e) => setCorporationData({ ...corporationData, name: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Nature of Business</span>
                        <input
                          type="text"
                          value={corporationData.natureOfBusiness}
                          onChange={(e) => setCorporationData({ ...corporationData, natureOfBusiness: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Director Names</span>
                        <input
                          type="text"
                          value={corporationData.directorNames}
                          onChange={(e) => setCorporationData({ ...corporationData, directorNames: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Verified</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            checked={corporationData.verified}
                            onChange={(e) => setCorporationData({ ...corporationData, verified: e.target.checked })}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <span style={{ fontSize: '13px' }}>{corporationData.verified ? 'Yes' : 'No'}</span>
                        </label>
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Verified Source</span>
                        <input
                          type="text"
                          value={corporationData.verifiedSource}
                          onChange={(e) => setCorporationData({ ...corporationData, verifiedSource: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Registration No</span>
                        <input
                          type="text"
                          value={corporationData.registrationNo}
                          onChange={(e) => setCorporationData({ ...corporationData, registrationNo: e.target.value })}
                          style={{ flex: 1, padding: '6px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '4px' }}
                        />
                      </div>
                      <div className="deal-info-row">
                        <span className="deal-info-label">Form Received</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            checked={corporationData.formReceived}
                            onChange={(e) => setCorporationData({ ...corporationData, formReceived: e.target.checked })}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <span style={{ fontSize: '13px' }}>{corporationData.formReceived ? 'Yes' : 'No'}</span>
                        </label>
                      </div>
                    </div>
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={handleCancelCorporation}
                        style={{ padding: '8px 16px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveCorporation}
                        disabled={savingCorporation}
                        style={{ padding: '8px 16px', fontSize: '13px', border: 'none', borderRadius: '6px', background: '#48182F', color: 'white', cursor: 'pointer' }}
                      >
                        {savingCorporation ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="deal-info-grid">
                    <div className="deal-info-row">
                      <span className="deal-info-label">Name</span>
                      <span className="deal-info-value">: {deal?.corporation?.name || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Nature of Business</span>
                      <span className="deal-info-value">: {deal?.corporation?.natureOfBusiness || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Director Names</span>
                      <span className="deal-info-value">: {deal?.corporation?.directorNames || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Verified</span>
                      <span className="deal-info-value">: {deal?.corporation?.verified ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Verified Source</span>
                      <span className="deal-info-value">: {deal?.corporation?.verifiedSource || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Registration No</span>
                      <span className="deal-info-value">: {deal?.corporation?.registrationNo || '—'}</span>
                    </div>
                    <div className="deal-info-row">
                      <span className="deal-info-label">Form Received</span>
                      <span className="deal-info-value">: {deal?.corporation?.formReceived ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes Tile */}
              <div className="deal-info-tile">
                <h3 className="deal-info-tile-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Notes
                  {!addingNote && (
                    <button
                      className="deal-info-edit-btn"
                      onClick={handleAddNote}
                      style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      + Add Note
                    </button>
                  )}
                </h3>
                <div style={{ padding: '16px' }}>
                  {addingNote && (
                    <div style={{ marginBottom: '16px', padding: '16px', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                      <textarea
                        value={noteContent}
                        onChange={(e) => setNoteContent(e.target.value)}
                        placeholder="Add your note here..."
                        rows={5}
                        style={{
                          width: '100%',
                          padding: '12px',
                          fontSize: '14px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          resize: 'vertical',
                          fontFamily: 'inherit'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
                        <button
                          onClick={handleCancelNote}
                          style={{ padding: '8px 16px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '6px', background: 'white', cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveNote}
                          disabled={savingNote || !noteContent.trim()}
                          style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            border: 'none',
                            borderRadius: '6px',
                            background: savingNote || !noteContent.trim() ? '#9CA3AF' : '#48182F',
                            color: 'white',
                            cursor: savingNote || !noteContent.trim() ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {savingNote ? 'Saving...' : 'Add'}
                        </button>
                      </div>
                    </div>
                  )}
                  {notes.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {notes.map((note) => (
                        <div
                          key={note._id}
                          style={{
                            padding: '12px',
                            background: '#F9FAFB',
                            borderRadius: '6px',
                            borderLeft: '3px solid #48182F'
                          }}
                        >
                          <p style={{
                            whiteSpace: 'pre-wrap',
                            marginBottom: '8px',
                            color: '#374151',
                            fontSize: '14px',
                            lineHeight: '1.5'
                          }}>
                            {note.content}
                          </p>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontWeight: '500' }}>
                              {note.createdByName || note.createdBy}
                            </span>
                            <span>•</span>
                            <span>
                              {new Date(note.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#6B7280', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                      No notes added yet
                    </p>
                  )}
                </div>
              </div>

              {/* Parking/Bicycle/Locker Tile */}
              <div className="deal-info-tile">
                <h3 className="deal-info-tile-title">Parking / Bicycle / Locker</h3>
                <div style={{ padding: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                    {/* Parking Section */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '2px solid #48182F' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
                          Parking Spaces
                        </h4>
                        <button
                          type="button"
                          onClick={handleOpenParkingModal}
                          disabled={loadingParkingTypes}
                          style={{
                            padding: '4px 12px',
                            fontSize: '12px',
                            border: '1px solid #48182F',
                            borderRadius: '4px',
                            background: 'white',
                            color: '#48182F',
                            cursor: loadingParkingTypes ? 'wait' : 'pointer',
                            fontWeight: '500',
                            opacity: loadingParkingTypes ? 0.6 : 1
                          }}
                        >
                          {loadingParkingTypes ? 'Loading...' : 'Allocate'}
                        </button>
                      </div>
                      {/* Show Allocated Parking */}
                      {deal && deal.allocateParking && deal.allocateParking.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                            Allocated (To Sell)
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {deal.allocateParking.map((allocation, idx) => (
                              <div
                                key={idx}
                                style={{
                                  padding: '10px',
                                  background: '#FEF3C7',
                                  border: '1px solid #FCD34D',
                                  borderRadius: '6px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: '600', color: '#92400E', fontSize: '13px' }}>
                                    {allocation.type}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#78350F' }}>
                                    Qty: {allocation.eligibleNo}
                                  </div>
                                </div>
                                <div style={{ fontWeight: '600', color: '#92400E', fontSize: '13px' }}>
                                  ${allocation.price.toLocaleString()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show Assigned Parking */}
                      {deal && deal.assignedParking && deal.assignedParking.length > 0 ? (
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                            Assigned Spaces
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {deal.assignedParking.map((parking, idx) => (
                              <div
                                key={idx}
                                style={{
                                  padding: '12px',
                                  background: '#F9FAFB',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '6px'
                                }}
                              >
                                <div style={{ fontWeight: '600', color: '#48182F', marginBottom: '4px' }}>
                                  {parking.parkingSpaceNumber || 'N/A'}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                  Type: {parking.parkingType || 'N/A'}
                                </div>
                                {parking.floorName && (
                                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                    Floor: {parking.floorName}
                                  </div>
                                )}
                                {parking.price > 0 && (
                                  <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500', marginTop: '4px' }}>
                                    ${parking.price.toLocaleString()}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        (!deal?.allocateParking || deal.allocateParking.length === 0) && (
                          <p style={{ color: '#6B7280', fontSize: '13px', fontStyle: 'italic' }}>
                            No parking allocated or assigned
                          </p>
                        )
                      )}
                    </div>

                    {/* Locker Section */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '2px solid #48182F' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
                          Lockers
                        </h4>
                        <button
                          type="button"
                          onClick={handleOpenLockerModal}
                          disabled={loadingLockerTypes}
                          style={{
                            padding: '4px 12px',
                            fontSize: '12px',
                            border: '1px solid #48182F',
                            borderRadius: '4px',
                            background: 'white',
                            color: '#48182F',
                            cursor: loadingLockerTypes ? 'wait' : 'pointer',
                            fontWeight: '500',
                            opacity: loadingLockerTypes ? 0.6 : 1
                          }}
                        >
                          {loadingLockerTypes ? 'Loading...' : 'Allocate'}
                        </button>
                      </div>
                      {/* Show Allocated Lockers */}
                      {deal && deal.allocateLocker && deal.allocateLocker.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                            Allocated (To Sell)
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {deal.allocateLocker.map((allocation, idx) => (
                              <div
                                key={idx}
                                style={{
                                  padding: '10px',
                                  background: '#FEF3C7',
                                  border: '1px solid #FCD34D',
                                  borderRadius: '6px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: '600', color: '#92400E', fontSize: '13px' }}>
                                    {allocation.type}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#78350F' }}>
                                    Qty: {allocation.eligibleNo}
                                  </div>
                                </div>
                                <div style={{ fontWeight: '600', color: '#92400E', fontSize: '13px' }}>
                                  ${allocation.price.toLocaleString()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show Assigned Lockers */}
                      {deal && deal.assignedLocker && deal.assignedLocker.length > 0 ? (
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                            Assigned Spaces
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {deal.assignedLocker.map((locker, idx) => (
                              <div
                                key={idx}
                                style={{
                                  padding: '12px',
                                  background: '#F9FAFB',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '6px'
                                }}
                              >
                                <div style={{ fontWeight: '600', color: '#48182F', marginBottom: '4px' }}>
                                  {locker.lockerSpaceNumber || 'N/A'}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                  Type: {locker.lockerType || 'N/A'}
                                </div>
                                {locker.floorName && (
                                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                    Floor: {locker.floorName}
                                  </div>
                                )}
                                {locker.price > 0 && (
                                  <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500', marginTop: '4px' }}>
                                    ${locker.price.toLocaleString()}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        (!deal?.allocateLocker || deal.allocateLocker.length === 0) && (
                          <p style={{ color: '#6B7280', fontSize: '13px', fontStyle: 'italic' }}>
                            No lockers allocated or assigned
                          </p>
                        )
                      )}
                    </div>

                    {/* Bicycle Section */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '2px solid #48182F' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>
                          Bicycle Spaces
                        </h4>
                        <button
                          type="button"
                          onClick={handleOpenBicycleModal}
                          disabled={loadingBicycleTypes}
                          style={{
                            padding: '4px 12px',
                            fontSize: '12px',
                            border: '1px solid #48182F',
                            borderRadius: '4px',
                            background: 'white',
                            color: '#48182F',
                            cursor: loadingBicycleTypes ? 'wait' : 'pointer',
                            fontWeight: '500',
                            opacity: loadingBicycleTypes ? 0.6 : 1
                          }}
                        >
                          {loadingBicycleTypes ? 'Loading...' : 'Allocate'}
                        </button>
                      </div>
                      {/* Show Allocated Bicycles */}
                      {deal && deal.allocateBicycle && deal.allocateBicycle.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                            Allocated (To Sell)
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {deal.allocateBicycle.map((allocation, idx) => (
                              <div
                                key={idx}
                                style={{
                                  padding: '10px',
                                  background: '#FEF3C7',
                                  border: '1px solid #FCD34D',
                                  borderRadius: '6px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: '600', color: '#92400E', fontSize: '13px' }}>
                                    {allocation.type}
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#78350F' }}>
                                    Qty: {allocation.eligibleNo}
                                  </div>
                                </div>
                                <div style={{ fontWeight: '600', color: '#92400E', fontSize: '13px' }}>
                                  ${allocation.price.toLocaleString()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Show Assigned Bicycles */}
                      {deal && deal.assignedBicycle && deal.assignedBicycle.length > 0 ? (
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
                            Assigned Spaces
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {deal.assignedBicycle.map((bicycle, idx) => (
                              <div
                                key={idx}
                                style={{
                                  padding: '12px',
                                  background: '#F9FAFB',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '6px'
                                }}
                              >
                                <div style={{ fontWeight: '600', color: '#48182F', marginBottom: '4px' }}>
                                  {bicycle.bicycleSpaceNumber || 'N/A'}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                  Type: {bicycle.bicycleType || 'N/A'}
                                </div>
                                {bicycle.areaName && (
                                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                    Area: {bicycle.areaName}
                                  </div>
                                )}
                                {bicycle.price > 0 && (
                                  <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: '500', marginTop: '4px' }}>
                                    ${bicycle.price.toLocaleString()}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        (!deal?.allocateBicycle || deal.allocateBicycle.length === 0) && (
                          <p style={{ color: '#6B7280', fontSize: '13px', fontStyle: 'italic' }}>
                            No bicycle spaces allocated or assigned
                          </p>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Unit History Tile */}
              {deal && deal.unitHistory && deal.unitHistory.length > 0 && (
                <div className="deal-info-tile" style={{ gridColumn: '1 / -1' }}>
                  <h3 className="deal-info-tile-title">Unit History</h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '16px',
                    padding: '16px'
                  }}>
                    {deal.unitHistory.map((history, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '16px',
                          background: '#F9FAFB',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px'
                        }}
                      >
                        <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '2px solid #48182F' }}>
                          <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                            Unit {history.unitNo}
                          </div>
                        </div>
                        <div className="deal-info-row" style={{ marginBottom: '6px' }}>
                          <span className="deal-info-label" style={{ fontSize: '12px' }}>Project</span>
                          <span className="deal-info-value" style={{ fontSize: '12px' }}>: {history.projectName}</span>
                        </div>
                        <div className="deal-info-row" style={{ marginBottom: '6px' }}>
                          <span className="deal-info-label" style={{ fontSize: '12px' }}>Model</span>
                          <span className="deal-info-value" style={{ fontSize: '12px' }}>: {history.modelName || '—'}</span>
                        </div>
                        <div className="deal-info-row" style={{ marginBottom: '6px' }}>
                          <span className="deal-info-label" style={{ fontSize: '12px' }}>Assigned On</span>
                          <span className="deal-info-value" style={{ fontSize: '12px' }}>: {new Date(history.assignedOn).toLocaleDateString()}</span>
                        </div>
                        {history.releasedOn && (
                          <>
                            <div className="deal-info-row" style={{ marginBottom: '6px' }}>
                              <span className="deal-info-label" style={{ fontSize: '12px' }}>Released On</span>
                              <span className="deal-info-value" style={{ fontSize: '12px' }}>: {new Date(history.releasedOn).toLocaleDateString()}</span>
                            </div>
                            <div className="deal-info-row" style={{ marginBottom: '6px' }}>
                              <span className="deal-info-label" style={{ fontSize: '12px' }}>Released By</span>
                              <span className="deal-info-value" style={{ fontSize: '12px' }}>: {history.releasedBy || '—'}</span>
                            </div>
                            <div className="deal-info-row">
                              <span className="deal-info-label" style={{ fontSize: '12px' }}>Reason</span>
                              <span className="deal-info-value" style={{ fontSize: '12px' }}>: {history.reason || '—'}</span>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'purchasers' && (
            <div className="deal-details-tab-content" style={{ padding: 0, display: 'block' }}>
              {/* Header with Add Button - Full Width */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '24px',
                background: 'white',
                borderRadius: '12px',
                marginBottom: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                  Purchasers {deal?.purchasers && deal.purchasers.length > 0 && `(${deal.purchasers.length})`}
                </h3>
                <button
                  onClick={addingPurchaser ? handleCancelAddPurchaser : handleAddPurchaser}
                  style={{
                    background: addingPurchaser ? 'white' : '#48182F',
                    color: addingPurchaser ? '#6B7280' : 'white',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    border: addingPurchaser ? '1px solid #D1D5DB' : 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  {addingPurchaser ? (
                    'Cancel'
                  ) : (
                    <>
                      <span style={{ fontSize: '18px' }}>+</span> Add Purchaser
                    </>
                  )}
                </button>
              </div>

              {/* Add Purchaser Form */}
              {addingPurchaser && (
                <div style={{
                  padding: '24px',
                  background: 'white',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                      Search and Add Purchaser
                    </h4>
                  </div>

                  <div style={{ position: 'relative', marginBottom: selectedPurchaser ? '16px' : '20px' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search by name or email..."
                      value={purchaserSearchQuery}
                      onChange={(e) => setPurchaserSearchQuery(e.target.value)}
                      autoFocus
                      style={{
                        width: '100%',
                        padding: '12px 40px 12px 12px',
                        fontSize: '14px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        background: 'white'
                      }}
                    />
                    {purchaserSearchLoading && (
                      <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                        <div className="spinner-small" />
                      </div>
                    )}
                    {purchaserSearchResults.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '250px',
                        overflowY: 'auto',
                        background: 'white',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        marginTop: '8px',
                        zIndex: 10,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      }}>
                        {purchaserSearchResults.map((contact) => (
                          <div
                            key={contact._id}
                            onClick={() => handleSelectPurchaser(contact)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #F3F4F6',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                          >
                            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#111827' }}>
                              {contact.displayName}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6B7280' }}>
                              {contact.emails?.[0]?.email} {contact.phones?.[0]?.number && `• ${contact.phones[0].number}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {selectedPurchaser && (
                    <div style={{
                      padding: '16px',
                      background: 'white',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      marginBottom: '20px'
                    }}>
                      <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px', fontWeight: 500 }}>
                        SELECTED PURCHASER
                      </div>
                      <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827', marginBottom: '4px' }}>
                        {selectedPurchaser.displayName}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6B7280' }}>
                        {selectedPurchaser.emails?.[0]?.email}
                        {selectedPurchaser.phones?.[0]?.number && ` • ${selectedPurchaser.phones[0].number}`}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={handleConfirmAddPurchaser}
                      disabled={!selectedPurchaser || savingPurchaser}
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: selectedPurchaser && !savingPurchaser ? '#48182F' : '#D1D5DB',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: selectedPurchaser && !savingPurchaser ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s'
                      }}
                    >
                      {savingPurchaser ? 'Adding...' : 'Add Purchaser'}
                    </button>
                    <button
                      onClick={handleCancelAddPurchaser}
                      disabled={savingPurchaser}
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: 'white',
                        color: '#6B7280',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: savingPurchaser ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Purchaser Cards */}
              {deal && deal.purchasers && deal.purchasers.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(550px, 1fr))',
                  gap: '24px'
                }}>
                  {deal.purchasers.map((purchaser, index) => (
                    <div
                      key={index}
                      style={{
                        background: 'white',
                        border: purchaser.isPrimary ? '2px solid #48182F' : 'none',
                        borderRadius: '12px',
                        padding: '24px',
                        boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)',
                        position: 'relative',
                        transition: 'all 0.2s'
                      }}
                    >
                      {/* Header with Primary Badge, Name and Three-Dot Menu */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '16px'
                      }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {/* Purchaser Name */}
                          <h4 style={{
                            margin: 0,
                            fontSize: '17px',
                            fontWeight: 700,
                            color: '#111827',
                            lineHeight: '1.3'
                          }}>
                            {purchaser.displayName || `${purchaser.firstName} ${purchaser.lastName}`}
                          </h4>

                          {/* Primary Badge */}
                          {purchaser.isPrimary && (
                            <div style={{
                              display: 'inline-block',
                              background: '#48182F',
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 700,
                              letterSpacing: '0.5px'
                            }}>
                              PRIMARY
                            </div>
                          )}
                        </div>

                        {/* Three-Dot Menu */}
                        <div data-purchaser-menu style={{ position: 'relative', marginLeft: '12px' }}>
                          <button
                            onClick={() => setPurchaserMenuOpen(purchaserMenuOpen === index ? null : index)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#48182F',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#D4CBC4';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            <MoreVertical size={20} />
                          </button>

                          {/* Dropdown Menu */}
                          {purchaserMenuOpen === index && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              marginTop: '4px',
                              background: 'white',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              minWidth: '160px',
                              zIndex: 10,
                              overflow: 'hidden'
                            }}>
                              {!purchaser.isPrimary && (
                                <button
                                  onClick={() => {
                                    handleSetPrimaryPurchaser(index);
                                    setPurchaserMenuOpen(null);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: 'none',
                                    background: 'white',
                                    color: '#374151',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#D4CBC4';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'white';
                                  }}
                                >
                                  Set Primary
                                </button>
                              )}
                              <a
                                href={`/workspaces/${workspaceId}/contacts?view=${purchaser.contactId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setPurchaserMenuOpen(null)}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '12px 16px',
                                  border: 'none',
                                  background: 'white',
                                  color: '#374151',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  textDecoration: 'none',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#D4CBC4';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'white';
                                }}
                              >
                                View Details
                              </a>
                              {!purchaser.isPrimary && (
                                <button
                                  onClick={() => {
                                    handleDeletePurchaser(index);
                                    setPurchaserMenuOpen(null);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: 'none',
                                    background: 'white',
                                    color: '#DC2626',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#FEE2E2';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'white';
                                  }}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{
                        height: '1px',
                        background: '#E5E7EB',
                        margin: '12px 0'
                      }} />

                      {/* Contact Information */}
                      <div style={{ marginBottom: '14px' }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: '#9CA3AF',
                          marginBottom: '8px',
                          letterSpacing: '0.5px'
                        }}>
                          CONTACT
                        </div>
                        {purchaser.email && (
                          <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'start', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#6B7280', minWidth: '50px' }}>Email</span>
                            <a href={`mailto:${purchaser.email}`} style={{
                              fontSize: '12px',
                              color: '#3B82F6',
                              textDecoration: 'none',
                              wordBreak: 'break-word'
                            }}>
                              {purchaser.email}
                            </a>
                          </div>
                        )}
                        {purchaser.phone && (
                          <div style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#6B7280', minWidth: '50px' }}>Phone</span>
                            <a href={`tel:${purchaser.phone}`} style={{
                              fontSize: '12px',
                              color: '#3B82F6',
                              textDecoration: 'none'
                            }}>
                              {purchaser.phone}
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Address */}
                      {purchaser.address && (purchaser.address.street1 || purchaser.address.city) && (
                        <div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#9CA3AF',
                            marginBottom: '8px',
                            letterSpacing: '0.5px'
                          }}>
                            ADDRESS
                          </div>
                          <div style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.5' }}>
                            {purchaser.address.street1 && <div>{purchaser.address.street1}</div>}
                            {purchaser.address.street2 && <div>{purchaser.address.street2}</div>}
                            {(purchaser.address.city || purchaser.address.state || purchaser.address.zip) && (
                              <div>
                                {purchaser.address.city && `${purchaser.address.city}`}
                                {purchaser.address.state && `, ${purchaser.address.state}`}
                                {purchaser.address.zip && ` ${purchaser.address.zip}`}
                              </div>
                            )}
                            {purchaser.address.country && <div>{purchaser.address.country}</div>}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                !addingPurchaser && (
                  <div style={{
                    padding: '80px 40px',
                    textAlign: 'center',
                    background: '#F9FAFB',
                    minHeight: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div>
                      <p style={{
                        color: '#9CA3AF',
                        fontSize: '15px',
                        margin: 0
                      }}>
                        No purchasers added yet. Click "Add Purchaser" to get started.
                      </p>
                    </div>
                  </div>
                )
              )}

              {/* Delete Purchaser Confirmation Modal */}
              {deletingPurchaserIndex !== null && deal?.purchasers && (
                <>
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.5)',
                      zIndex: 1000,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={handleCancelDeletePurchaser}
                  />
                  <div
                    style={{
                      position: 'fixed',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      zIndex: 1001,
                      width: '90%',
                      maxWidth: '450px',
                      padding: '24px'
                    }}
                  >
                    <h3 style={{
                      margin: '0 0 16px 0',
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#111827'
                    }}>
                      Remove Purchaser
                    </h3>
                    <p style={{
                      margin: '0 0 24px 0',
                      fontSize: '14px',
                      color: '#6B7280',
                      lineHeight: '1.5'
                    }}>
                      Are you sure you want to remove <strong>{deal.purchasers[deletingPurchaserIndex]?.displayName || `${deal.purchasers[deletingPurchaserIndex]?.firstName} ${deal.purchasers[deletingPurchaserIndex]?.lastName}`}</strong> from this deal?
                    </p>
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      justifyContent: 'flex-end'
                    }}>
                      <button
                        onClick={handleCancelDeletePurchaser}
                        disabled={deletingPurchaserLoading}
                        style={{
                          padding: '10px 20px',
                          background: 'white',
                          color: '#6B7280',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: deletingPurchaserLoading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s',
                          opacity: deletingPurchaserLoading ? 0.5 : 1
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmDeletePurchaser}
                        disabled={deletingPurchaserLoading}
                        style={{
                          padding: '10px 20px',
                          background: '#DC2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: deletingPurchaserLoading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s',
                          opacity: deletingPurchaserLoading ? 0.7 : 1
                        }}
                      >
                        {deletingPurchaserLoading ? 'Removing...' : 'Remove'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {activeTab === 'purchaser-portal' && (
            <div className="deal-details-tab-content" style={{ padding: 0, display: 'block' }}>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <h3 style={{
                  margin: '0 0 20px 0',
                  paddingBottom: '16px',
                  borderBottom: '1px solid #E5E7EB',
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#48182F'
                }}>
                  Purchaser Portal
                </h3>
                {deal?.unitId ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    padding: '24px',
                    background: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #48182F 0%, #6B2847 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <FileText size={24} style={{ color: 'white' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          margin: '0 0 4px 0',
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#111827'
                        }}>
                          View Unit Deal Details
                        </h4>
                        <p style={{
                          margin: 0,
                          fontSize: '14px',
                          color: '#6B7280'
                        }}>
                          Access comprehensive unit details, documents, and purchaser portal features
                        </p>
                      </div>
                    </div>
                    <a
                      href={`/workspaces/${workspaceId}/purchaser-portal/unit-deal?unitId=${deal.unitId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #48182F 0%, #6B2847 100%)',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(72, 24, 47, 0.2)',
                        alignSelf: 'flex-start'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(72, 24, 47, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(72, 24, 47, 0.2)';
                      }}
                    >
                      <FileText size={18} />
                      Open Unit Deal Portal
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </a>
                  </div>
                ) : (
                  <div style={{
                    padding: '24px',
                    textAlign: 'center',
                    background: '#FEF3C7',
                    borderRadius: '8px',
                    border: '1px solid #FCD34D'
                  }}>
                    <p style={{ margin: 0, color: '#92400E', fontSize: '14px' }}>
                      This deal is not associated with a unit. Unit Deal Portal is not available.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="deal-details-tab-content" style={{ padding: 0, display: 'block' }}>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                {deal ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Décor Studio Amendments */}
                    <div style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: '#F9FAFB'
                    }}>
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={deal._id}
                        fileType="decor_studio_amendment"
                        acceptTypes=".pdf,.doc,.docx"
                        title="Décor Studio Amendments"
                      />
                    </div>

                    {/* Preferred Parking Amendments */}
                    <div style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: '#F9FAFB'
                    }}>
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={deal._id}
                        fileType="preferred_parking_amendment"
                        acceptTypes=".pdf,.doc,.docx"
                        title="Preferred Parking Amendments"
                      />
                    </div>

                    {/* PDI */}
                    <div style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: '#F9FAFB'
                    }}>
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={deal._id}
                        fileType="pdi"
                        acceptTypes=".pdf,.doc,.docx"
                        title="PDI"
                      />
                    </div>

                    {/* CCP */}
                    <div style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: '#F9FAFB'
                    }}>
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={deal._id}
                        fileType="ccp"
                        acceptTypes=".pdf,.doc,.docx"
                        title="CCP"
                      />
                    </div>

                    {/* FAQ */}
                    <div style={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: '#F9FAFB'
                    }}>
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={deal._id}
                        fileType="faq"
                        acceptTypes=".pdf,.doc,.docx"
                        title="FAQ"
                      />
                    </div>
                  </div>
                ) : (
                  <div style={{
                    padding: '24px',
                    textAlign: 'center',
                    color: '#6B7280'
                  }}>
                    <p>No deal loaded. Documents cannot be uploaded.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Release Unit Confirmation Modal */}
      {showReleaseConfirm && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 3000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={handleCancelReleaseUnit}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              zIndex: 3001,
              minWidth: '400px',
              maxWidth: '500px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600 }}>Confirm Release Unit</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6B7280' }}>
              Are you sure you want to release unit <strong>{deal?.unitNo}</strong> from this deal?
              This action will move the unit to history and change the deal stage to "UNIT RELEASED".
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                Reason (optional):
              </label>
              <textarea
                value={releaseReason}
                onChange={(e) => setReleaseReason(e.target.value)}
                placeholder="Enter reason for releasing the unit..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '13px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancelReleaseUnit}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReleaseUnit}
                disabled={releasingUnit}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#DC2626',
                  color: 'white',
                  cursor: releasingUnit ? 'not-allowed' : 'pointer',
                  opacity: releasingUnit ? 0.6 : 1
                }}
              >
                {releasingUnit ? 'Releasing...' : 'Release Unit'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Parking Allocation Modal */}
      {showParkingModal && ReactDOM.createPortal(
        <div
          id="parking-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowParkingModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}
          onClick={(e) => {
            console.log('Modal container clicked');
            e.stopPropagation();
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>
              Allocate Parking (Sell Parking)
            </h3>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#F3F4F6', borderRadius: '6px' }}>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>
                <div>Eligible: <span style={{ fontWeight: '600', color: '#111827' }}>{deal?.isParkingEligible ? 'Yes' : 'No'}</span></div>
                <div>Max Parking: <span style={{ fontWeight: '600', color: '#111827' }}>{deal?.maxParking || 0}</span></div>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#111827' }}>Type</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#111827' }}>Number</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#111827' }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {parkingAllocations.map((allocation, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={{ padding: '8px' }}>
                      <div style={{ padding: '6px 8px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {allocation.type}
                      </div>
                    </td>
                    <td style={{ padding: '8px' }}>
                      <input
                        type="number"
                        value={allocation.number}
                        onChange={(e) => {
                          const newAllocations = [...parkingAllocations];
                          newAllocations[index].number = parseInt(e.target.value) || 0;
                          setParkingAllocations(newAllocations);
                        }}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px' }}
                      />
                    </td>
                    <td style={{ padding: '8px' }}>
                      <input
                        type="number"
                        value={allocation.price}
                        onChange={(e) => {
                          const newAllocations = [...parkingAllocations];
                          newAllocations[index].price = parseFloat(e.target.value) || 0;
                          setParkingAllocations(newAllocations);
                        }}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={() => setShowParkingModal(false)}
                disabled={savingAllocation}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#6B7280',
                  cursor: savingAllocation ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveParkingAllocation}
                disabled={savingAllocation}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#48182F',
                  color: 'white',
                  cursor: savingAllocation ? 'not-allowed' : 'pointer',
                  opacity: savingAllocation ? 0.6 : 1,
                  fontWeight: '500'
                }}
              >
                {savingAllocation ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Locker Allocation Modal */}
      {showLockerModal && ReactDOM.createPortal(
        <div
          id="locker-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowLockerModal(false)}>
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '600px',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>
              Allocate Locker (Sell Locker)
            </h3>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#F3F4F6', borderRadius: '6px' }}>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>
                <div>Eligible: <span style={{ fontWeight: '600', color: '#111827' }}>{deal?.isLockerEligible ? 'Yes' : 'No'}</span></div>
                <div>Max Lockers: <span style={{ fontWeight: '600', color: '#111827' }}>{deal?.maxLockers || 0}</span></div>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#111827' }}>Type</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#111827' }}>Number</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#111827' }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {lockerAllocations.map((allocation, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={{ padding: '8px' }}>
                      <div style={{ padding: '6px 8px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {allocation.type}
                      </div>
                    </td>
                    <td style={{ padding: '8px' }}>
                      <input
                        type="number"
                        value={allocation.number}
                        onChange={(e) => {
                          const newAllocations = [...lockerAllocations];
                          newAllocations[index].number = parseInt(e.target.value) || 0;
                          setLockerAllocations(newAllocations);
                        }}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px' }}
                      />
                    </td>
                    <td style={{ padding: '8px' }}>
                      <input
                        type="number"
                        value={allocation.price}
                        onChange={(e) => {
                          const newAllocations = [...lockerAllocations];
                          newAllocations[index].price = parseFloat(e.target.value) || 0;
                          setLockerAllocations(newAllocations);
                        }}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={() => setShowLockerModal(false)}
                disabled={savingAllocation}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#6B7280',
                  cursor: savingAllocation ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLockerAllocation}
                disabled={savingAllocation}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#48182F',
                  color: 'white',
                  cursor: savingAllocation ? 'not-allowed' : 'pointer',
                  opacity: savingAllocation ? 0.6 : 1,
                  fontWeight: '500'
                }}
              >
                {savingAllocation ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bicycle Allocation Modal */}
      {showBicycleModal && ReactDOM.createPortal(
        <div
          id="bicycle-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowBicycleModal(false)}>
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '600px',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>
              Allocate Bicycle (Sell Bicycle)
            </h3>

            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#F3F4F6', borderRadius: '6px' }}>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>
                <div>Eligible: <span style={{ fontWeight: '600', color: '#111827' }}>{deal?.isBicycleEligible ? 'Yes' : 'No'}</span></div>
                <div>Max Bicycle: <span style={{ fontWeight: '600', color: '#111827' }}>{deal?.maxBicycle || 0}</span></div>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#111827' }}>Type</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#111827' }}>Number</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#111827' }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {bicycleAllocations.map((allocation, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #E5E7EB' }}>
                    <td style={{ padding: '8px' }}>
                      <div style={{ padding: '6px 8px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {allocation.type}
                      </div>
                    </td>
                    <td style={{ padding: '8px' }}>
                      <input
                        type="number"
                        value={allocation.number}
                        onChange={(e) => {
                          const newAllocations = [...bicycleAllocations];
                          newAllocations[index].number = parseInt(e.target.value) || 0;
                          setBicycleAllocations(newAllocations);
                        }}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px' }}
                      />
                    </td>
                    <td style={{ padding: '8px' }}>
                      <input
                        type="number"
                        value={allocation.price}
                        onChange={(e) => {
                          const newAllocations = [...bicycleAllocations];
                          newAllocations[index].price = parseFloat(e.target.value) || 0;
                          setBicycleAllocations(newAllocations);
                        }}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '4px', fontSize: '14px' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={() => setShowBicycleModal(false)}
                disabled={savingAllocation}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#6B7280',
                  cursor: savingAllocation ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBicycleAllocation}
                disabled={savingAllocation}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#48182F',
                  color: 'white',
                  cursor: savingAllocation ? 'not-allowed' : 'pointer',
                  opacity: savingAllocation ? 0.6 : 1,
                  fontWeight: '500'
                }}
              >
                {savingAllocation ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default DealDetailsSlider;
